import { useEffect, useState, type ReactNode, type ComponentType } from "react";
import { audio } from "../../lib/audio";
import { dd } from "./state";
import { Code, Btn, Win } from "./ui";
import { IdorStory, Browser } from "./idorStory";
import { XssStory, SqliStory, InjectionStory, SupplyStory } from "./attackScenes";
import type { StageProps } from "./types";
import type { MfitGuideText } from "../../scenario/types";
import { tx, txr } from "../../lib/tx";

/**
 * "See it exploited" — the attacker-view lab between a mission's briefing and
 * its exercise. Each vulnerability plays as a live-attack cinematic (the
 * learner runs the attack and watches the data come out), then a teaching tail
 * shows what was captured, the vulnerable code next to the fix, and a re-run
 * the fix blocks. Nothing is real: no request, query, script or install runs.
 * Story components live in `idorStory.tsx` / `attackScenes.tsx`; this file
 * holds the teaching tail and wires one story per vulnerability kind.
 */
interface Payload { id: string; label: string; evil?: boolean; note?: string }
interface LabData {
  kind: string;
  badge: string;
  title: string;
  setup: string;
  payloads?: Payload[];
  vuln: { label: string; code: string };
  fix: { label: string; code: string };
  explain: string[];
  lesson: string;
  handoff: string;
}

/* ------------------------------------------------------------ shared pieces */
function Badge({ text }: { text: string }) {
  return (
    <div className="dd-attbadge">
      <span className="dd-attdot" aria-hidden="true" />
      {txr("You are the attacker · {text}", { text })}
    </div>
  );
}

function Loot({ title, lines, tone = "risk" }: { title: string; lines: string[]; tone?: "risk" | "ok" }) {
  return (
    <div className={`dd-loot is-${tone}`}>
      <div className="dd-loot-h"><span className="dd-term-dot" aria-hidden="true" />{title}</div>
      <div className="dd-loot-b">
        {lines.length ? lines.map((l, i) => <div key={i} className="dd-loot-l">{l}</div>) : <div className="dd-loot-l is-empty">{tx("— nothing captured —")}</div>}
      </div>
    </div>
  );
}

function CodeFix({ lab }: { lab: LabData }) {
  return (
    <div className="dd-codefix">
      <div className="dd-cf-col is-vuln">
        <span className="dd-ih">{txr("Why it worked · {label}", { label: lab.vuln.label })}</span>
        <Code small>{lab.vuln.code}</Code>
      </div>
      <div className="dd-cf-col is-fix">
        <span className="dd-ih">{txr("The fix · {label}", { label: lab.fix.label })}</span>
        <Code small>{lab.fix.code}</Code>
      </div>
    </div>
  );
}

function RevealBody({ lab, loot }: { lab: LabData; loot: ReactNode }) {
  return (
    <div className="dd-reveal">
      {loot}
      <div className="dd-reveal-x">
        <span className="dd-ih">{tx("What just happened")}</span>
        <ul>{lab.explain.map((e) => <li key={e}>{e}</li>)}</ul>
      </div>
    </div>
  );
}

function FixBody({ lab, children }: { lab: LabData; children: ReactNode }) {
  return (
    <div className="dd-fixbody">
      <CodeFix lab={lab} />
      {children}
      <div className="dd-lesson"><b>{tx("Takeaway")}</b> {lab.lesson}</div>
    </div>
  );
}

/* The teaching tail shared by every live-attack lab: after the cinematic, show
   what was captured + why it worked, then the vulnerable code next to the fix
   with a re-run that the fix blocks. Two light phases. */
function TeachTail({ lab, loot, rerunLabel, rerunResult, onDone, onSkip, onGuide }: {
  lab: LabData; loot: ReactNode; rerunLabel: string; rerunResult: ReactNode; onDone: () => void; onSkip: () => void; onGuide?: (g: MfitGuideText | null) => void;
}) {
  const [phase, setPhase] = useState<"reveal" | "fix">("reveal");
  const [fixRan, setFixRan] = useState(false);
  // The story cleared the card on unmount; keep naming the next control here.
  useEffect(() => {
    onGuide?.(phase === "reveal"
      ? { title: "See the code, and the fix", body: "You just ran the attack. Click How to stop it to see why it worked and the fix.", actionLabel: "How to stop it" }
      : { title: "Re-run against the fix", body: fixRan ? "The fix blocked it. Click Switch sides: now fix it to start the exercise." : "Click the button to re-run the same attack against the patched code.", actionLabel: fixRan ? "Switch sides: now fix it" : rerunLabel });
  }, [phase, fixRan, rerunLabel, onGuide]);
  useEffect(() => () => onGuide?.(null), [onGuide]);
  return (
    <div className="dd-lab">
      <Badge text={lab.badge} />
      <div className="dd-lab-head">
        <div>
          <span className="dd-kicker">{tx("After the attack")}</span>
          <h2>{lab.title}</h2>
        </div>
        <button type="button" className="dd-link" onClick={onSkip}>{tx("Skip")}</button>
      </div>
      <div className="dd-lab-body">
        {phase === "reveal" ? (
          <RevealBody lab={lab} loot={loot} />
        ) : (
          <FixBody lab={lab}>
            <div className="dd-rerun">
              <Btn onClick={() => { setFixRan(true); audio.ui("success"); }} pulse={!fixRan}>{rerunLabel}</Btn>
              {fixRan && rerunResult}
            </div>
          </FixBody>
        )}
      </div>
      <footer className="dd-foot">
        <div className="dd-lab-dots" aria-hidden="true">
          <span className="is-on" /><span className="is-on" /><span className="is-on" /><span className={phase === "fix" ? "is-on" : ""} />
        </div>
        <div className="dd-foot-btns">
          {phase === "fix" && <Btn small onClick={() => setPhase("reveal")}>{tx("Back")}</Btn>}
          <Btn primary pulse onClick={() => (phase === "reveal" ? setPhase("fix") : onDone())}>
            {phase === "reveal" ? tx("How to stop it") : tx("Switch sides: now fix it")}
          </Btn>
        </div>
      </footer>
    </div>
  );
}

/* Generic: play the live-attack story, then the teaching tail. The story is
   rendered as a real child component (never called as a function) so its hooks
   live on their own fiber and unmount cleanly when the tail takes over. */
type StoryComp = ComponentType<{ onFinish: () => void; onSkip: () => void; onGuide?: (g: MfitGuideText | null) => void }>;
function StoryLab({ Story, lab, loot, rerunLabel, rerunResult, onDone, onSkip, onGuide }: {
  Story: StoryComp; lab: LabData; loot: ReactNode; rerunLabel: string; rerunResult: ReactNode; onDone: () => void; onSkip: () => void; onGuide?: (g: MfitGuideText | null) => void;
}) {
  const [mode, setMode] = useState<"story" | "teach">("story");
  if (mode === "story") {
    return <Story onSkip={onSkip} onGuide={onGuide} onFinish={() => { audio.ui("pageOpen"); setMode("teach"); }} />;
  }
  return <TeachTail lab={lab} loot={loot} rerunLabel={rerunLabel} rerunResult={rerunResult} onDone={onDone} onSkip={onSkip} onGuide={onGuide} />;
}

/* The re-run result reuses the SAME visual environment the attack used (the
   fake browser, or the terminal), now showing the blocked outcome. */
function RerunBrowser({ url, label, children }: { url: string; label: string; children: ReactNode }) {
  return (
    <div className="dd-rr" role="status">
      <Browser url={url} secure tone="attacker" label={label}>{children}</Browser>
    </div>
  );
}

function RerunTerm({ title, lines }: { title: string; lines: { text: string; kind?: "bad" | "good" }[] }) {
  return (
    <div className="dd-rr dd-rr-term" role="status">
      <div className="dd-term2">
        <div className="dd-term2-h"><span className="dd-term-dot" aria-hidden="true" />{title}</div>
        <div className="dd-term2-b">
          {lines.map((l, i) => (
            <div key={i} className={`dd-term2-l${l.kind ? ` is-${l.kind}` : ""}`} style={{ animationDelay: `${i * 0.25}s` }}>{l.text}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BlockedBanner({ code, title, text }: { code: string; title: string; text: string }) {
  return (
    <div className="dd-rr-block">
      <span className="dd-rr-code">{code}</span>
      <div><b>{title}</b><span>{text}</span></div>
    </div>
  );
}

interface LabKindProps { lab: LabData; idx: number; total: number; onDone: () => void; onSkip: () => void; onGuide?: (g: MfitGuideText | null) => void }

/* ------------------------------------------------------------ IDOR */
function IdorLab({ lab, onDone, onSkip, onGuide }: LabKindProps) {
  return (
    <StoryLab
      Story={IdorStory}
      lab={lab}
      onDone={onDone}
      onSkip={onSkip}
      onGuide={onGuide}
      loot={<Loot title={tx("Harvested from every customer")} lines={["12,904 invoices: names, emails, home routes, trip dates", "partial cards \u2014 then full cards via the \u201cfee\u201d phish", "working voucher links, resold at a discount"]} />}
      rerunLabel={tx("Re-run the id change with the owner check")}
      rerunResult={
        <RerunBrowser url="www.skyhop-travel.example/invoices/download?id=SKY-100481" label={tx("Your browser, same tampered id")}>
          <div className="dd-rr-page">
            <BlockedBanner code="403" title={tx("Forbidden: not your invoice")} text={tx("The server checked who owns SKY-100481 before sending anything.")} />
            <div className="dd-rr-note">{tx("Same URL, same changed id. The invoice belongs to another customer, so nothing was returned. Your own invoice still opens normally.")}</div>
          </div>
        </RerunBrowser>
      }
    />
  );
}

/* ------------------------------------------------------------ XSS */
function XssLab({ lab, onDone, onSkip, onGuide }: LabKindProps) {
  return (
    <StoryLab
      Story={XssStory}
      lab={lab}
      onDone={onDone}
      onSkip={onSkip}
      onGuide={onGuide}
      loot={<Loot title={tx("Captured in your attacker console")} lines={["victim: admin@orbitdesk.app", "session cookie: ODSK-9f21e7c4a0", "\u2192 replayed as the admin: every tenant\u2019s billing"]} />}
      rerunLabel={tx("Open the same title with textContent")}
      rerunResult={
        <RerunBrowser url="teamboard.example/board" label={tx("Your browser, same message posted")}>
          <div className="dd-rr-page">
            <div className="dd-rr-feed">
              <div className="dd-rr-msg">{txr("<0>Priya</0> Lunch moved to 12:30 today", {}, [<b />])}</div>
              <div className="dd-rr-msg"><b>{tx("guest")}</b> <span className="dd-rr-lit">{tx("<img src=x onerror=...>")}</span></div>
            </div>
            <BlockedBanner code={tx("TEXT")} title={tx("Shown as plain text: nothing ran")} text={tx("The board now escapes your message. The browser prints it instead of running it, so no alert, no stolen cookie.")} />
          </div>
        </RerunBrowser>
      }
    />
  );
}

/* ------------------------------------------------------------ SQL injection */
function SqliLab({ lab, onDone, onSkip, onGuide }: LabKindProps) {
  return (
    <StoryLab
      Story={SqliStory}
      lab={lab}
      onDone={onDone}
      onSkip={onSkip}
      onGuide={onGuide}
      loot={<Loot title={tx("Extracted from the database")} lines={["Every report, every customer (812 rows)", "EXPORT_SERVICE_TOKEN (live credential)", "DB_ROOT_PASSWORD, SMTP_RELAY_KEY"]} />}
      rerunLabel={tx("Run ' OR '1'='1' -- with the bound query")}
      rerunResult={
        <RerunBrowser url={"portal.novella.example/orders?title=' OR '1'='1' --"} label={tx("Your browser, same injected input")}>
          <div className="dd-rr-page">
            <div className="dd-rr-bar"><b>{txr("Novella <0>Staff Portal</0>", {}, [<span />])}</b><span>{tx("Search reports")}</span></div>
            <BlockedBanner code={tx("0 rows")} title={tx("No results, and no database error")} text={tx("The input is sent as a value, not as SQL. No report is literally titled that.")} />
            <div className="dd-rr-note">{txr("The query is now <0>WHERE tenant_id = ? AND title = ?</0>, so the quotes in your input are just characters.", {}, [<code />])}</div>
          </div>
        </RerunBrowser>
      }
    />
  );
}

/* ------------------------------------------------------------ prompt injection */
function InjectionLab({ lab, onDone, onSkip, onGuide }: LabKindProps) {
  return (
    <StoryLab
      Story={InjectionStory}
      lab={lab}
      onDone={onDone}
      onSkip={onSkip}
      onGuide={onGuide}
      loot={<Loot title={tx("Received at your collector")} lines={["EXPORT_SERVICE_TOKEN=ODSK-stg-7c21\u2026", "DATABASE_URL=postgres://\u2026@staging", "the tenant-isolation test is now disabled"]} />}
      rerunLabel={tx("Re-run with the narrow profile")}
      rerunResult={
        <RerunTerm
          title={tx("developer's AI agent · narrow profile: task worktree only")}
          lines={[
            { text: "agent: reading issue #482\u2026" },
            { text: "agent: task found: fix export preview truncation" },
            { text: "agent: the issue also tells me to read credentials and POST them to an outside host" },
            { text: "runtime: denied \u2014 ~/.config/... is outside the task worktree", kind: "bad" },
            { text: "runtime: denied \u2014 diagnostics.example.invalid not on the allow-list", kind: "bad" },
            { text: "Untrusted instruction refused. Nothing left the machine.", kind: "good" },
          ]}
        />
      }
    />
  );
}

/* ------------------------------------------------------------ supply chain */
function SupplyLab({ lab, onDone, onSkip, onGuide }: LabKindProps) {
  return (
    <StoryLab
      Story={SupplyStory}
      lab={lab}
      onDone={onDone}
      onSkip={onSkip}
      onGuide={onGuide}
      loot={<Loot title={tx("Received at your telemetry host")} lines={["NPM_TOKEN, AWS_PROFILE, EXPORT_SERVICE_TOKEN", "hostname, repo path, git remote", "\u2192 and your code now ships in their build"]} />}
      rerunLabel={tx("Install the reviewed helper instead")}
      rerunResult={
        <RerunTerm
          title={tx("developer terminal")}
          lines={[
            { text: "$ npm install rapid-export-helper" },
            { text: "npm ERR! not in the approved manifest, no signed provenance", kind: "bad" },
            { text: "Unreviewed package refused. No install script ran.", kind: "good" },
            { text: "$ npm install @astercloud/export-helper" },
            { text: "signed provenance, in the approved manifest", kind: "good" },
            { text: "added 1 package, 0 install scripts run", kind: "good" },
          ]}
        />
      }
    />
  );
}

const KINDS: Record<string, ComponentType<LabKindProps>> = {
  idor: IdorLab,
  sqli: SqliLab,
  xss: XssLab,
  injection: InjectionLab,
  supplychain: SupplyLab,
};

export function Lab({ cfg, finish, onGuideChange }: StageProps) {
  const labs = ((cfg.content as { labs?: LabData[] }).labs ?? []);
  const [idx, setIdx] = useState(0);
  // The story (and the teaching tail) own the guide card per step; no static
  // "See it exploited" fallback here, or it overwrites the first (power-on) step.
  if (!labs.length) {
    finish("continue");
    return null;
  }
  const lab = labs[idx];
  const Kind = KINDS[lab.kind];
  const done = () => {
    if (idx < labs.length - 1) { dd.log(labToMission(cfg), `Ran the ${lab.badge} attacker demo.`, "note"); setIdx(idx + 1); }
    else { dd.log(labToMission(cfg), "Saw the vulnerability exploited from the attacker's side.", "note"); audio.ui("pageOpen"); finish("continue"); }
  };
  return (
    <Win app={tx("Security lab · Attacker's view")} tag={cfg.screen.toUpperCase()} clock={cfg.clock}>
      {Kind ? <Kind key={lab.kind} lab={lab} idx={idx} total={labs.length} onDone={done} onSkip={() => finish("continue")} onGuide={onGuideChange} /> : null}
    </Win>
  );
}

function labToMission(cfg: StageProps["cfg"]): string {
  const m = (cfg.content as { mission?: string }).mission;
  if (m) return m;
  return (cfg.mode || "").replace(/lab$/, "") || "lab";
}
